export interface SongLink {
  spotify: string;
  bandcamp?: string;
  tidal?: string;
  apple?: string;
  youtube?: string;
}

// /listen?song=<slug> redirects straight to the matching entry's Spotify URL,
// skipping the linktree page. An empty/missing spotify value falls back to
// the linktree instead of redirecting to a blank URL.
//
// Sourced from DistroKid (2026-07-29) for the "Muted" album and its 23
// tracks. "album" points at the full album rather than a single track.
// bandcamp/tidal/apple/youtube URLs added 2026-07-29 — not wired into the
// redirect yet, just stored for now. Tidal IDs confirmed sequential from the
// album ID (verified against tracks 1, 2, 11, and 23 on tidal.com). Apple
// and YouTube IDs are not sequential/predictable — pulled directly from each
// platform's album/playlist page.
const songLinks: Record<string, SongLink> = {
  album: {
    spotify: 'https://open.spotify.com/album/5uEU9DKaSGWnCEyk99qnf2',
    apple: 'https://music.apple.com/us/album/muted/1697299004',
    youtube: 'https://music.youtube.com/playlist?list=OLAK5uy_nj79d7azNoXuDggCJKJQHkrPhmRF5CgzA',
  },
  finite: {
    spotify: 'https://open.spotify.com/track/0pSoHsqCU0oOUGiDpmSghF',
    bandcamp: 'https://brinary.bandcamp.com/track/finite',
    tidal: 'https://tidal.com/track/304673506/u',
    apple: 'https://music.apple.com/us/song/finite/1697299005',
    youtube: 'https://music.youtube.com/watch?v=oss6_PgXFqE',
  },
  beams: {
    spotify: 'https://open.spotify.com/track/12QmE2AWv44ZpFf7nKy5Ly',
    bandcamp: 'https://brinary.bandcamp.com/track/beams',
    tidal: 'https://tidal.com/track/304673507/u',
    apple: 'https://music.apple.com/us/song/beams/1697299307',
    youtube: 'https://music.youtube.com/watch?v=qL883-o-FLQ',
  },
  'the-haze': {
    spotify: 'https://open.spotify.com/track/4kY9ynCG6BeX30b3MT5WLP',
    bandcamp: 'https://brinary.bandcamp.com/track/the-haze',
    tidal: 'https://tidal.com/track/304673508/u',
    apple: 'https://music.apple.com/us/song/the-haze/1697299308',
    youtube: 'https://music.youtube.com/watch?v=6tkgu_jfR3w',
  },
  realigned: {
    spotify: 'https://open.spotify.com/track/70x6Y7bMUtE1C4QdOH3kVX',
    bandcamp: 'https://brinary.bandcamp.com/track/realigned',
    tidal: 'https://tidal.com/track/304673509/u',
    apple: 'https://music.apple.com/us/song/realigned/1697299309',
    youtube: 'https://music.youtube.com/watch?v=BZharGGVnC4',
  },
  null: {
    spotify: 'https://open.spotify.com/track/06hZ0jkoFJmCkZRDWJEBrs',
    bandcamp: 'https://brinary.bandcamp.com/track/null',
    tidal: 'https://tidal.com/track/304673510/u',
    apple: 'https://music.apple.com/us/song/null/1697299310',
    youtube: 'https://music.youtube.com/watch?v=a6u1W9SFS7s',
  },
  flowerpot: {
    spotify: 'https://open.spotify.com/track/6H5uKb4lja9BPQE20hWCO8',
    bandcamp: 'https://brinary.bandcamp.com/track/flowerpot',
    tidal: 'https://tidal.com/track/304673511/u',
    apple: 'https://music.apple.com/us/song/flowerpot/1697299311',
    youtube: 'https://music.youtube.com/watch?v=v9AnyEUvPbY',
  },
  '7-of-7': {
    spotify: 'https://open.spotify.com/track/7HetVA79odO9Oja1wM9s5a',
    bandcamp: 'https://brinary.bandcamp.com/track/7-of-7',
    tidal: 'https://tidal.com/track/304673512/u',
    apple: 'https://music.apple.com/us/song/7-of-7/1697299312',
    youtube: 'https://music.youtube.com/watch?v=wVRHl22-K60',
  },
  'losing-things': {
    spotify: 'https://open.spotify.com/track/16YjGmyoKoL2cWMMhJu2vK',
    bandcamp: 'https://brinary.bandcamp.com/track/losing-things',
    tidal: 'https://tidal.com/track/304673513/u',
    apple: 'https://music.apple.com/us/song/losing-things/1697299313',
    youtube: 'https://music.youtube.com/watch?v=xQhr3LMjKFc',
  },
  'asteroid-with-a-face': {
    spotify: 'https://open.spotify.com/track/0CqrBVFaefmNbhGxVt9EyF',
    bandcamp: 'https://brinary.bandcamp.com/track/asteroid-with-a-face',
    tidal: 'https://tidal.com/track/304673514/u',
    apple: 'https://music.apple.com/us/song/asteroid-with-a-face/1697299314',
    youtube: 'https://music.youtube.com/watch?v=Rsm90x7GJaI',
  },
  torrid: {
    spotify: 'https://open.spotify.com/track/0gJyYma6sMuRJoA55X471J',
    bandcamp: 'https://brinary.bandcamp.com/track/torrid',
    tidal: 'https://tidal.com/track/304673515/u',
    apple: 'https://music.apple.com/us/song/torrid/1697299315',
    youtube: 'https://music.youtube.com/watch?v=QQuKpbYDFQ4',
  },
  'drip-city': {
    spotify: 'https://open.spotify.com/track/6sgQPsFFJJdhoyVGc5PssX',
    bandcamp: 'https://brinary.bandcamp.com/track/drip-city',
    tidal: 'https://tidal.com/track/304673516/u',
    apple: 'https://music.apple.com/us/song/drip-city/1697299316',
    youtube: 'https://music.youtube.com/watch?v=bR15G1Iyl3A',
  },
  'alone-in-the-waves': {
    spotify: 'https://open.spotify.com/track/63sEkOgRKlj0dPlHg8z9Ow',
    bandcamp: 'https://brinary.bandcamp.com/track/alone-in-the-waves',
    tidal: 'https://tidal.com/track/304673517/u',
    apple: 'https://music.apple.com/us/song/alone-in-the-waves/1697299317',
    youtube: 'https://music.youtube.com/watch?v=8ylCINGHGSQ',
  },
  'inner-orbit': {
    spotify: 'https://open.spotify.com/track/1uZYXeUGJLrYTmNcs0YULX',
    bandcamp: 'https://brinary.bandcamp.com/track/inner-orbit',
    tidal: 'https://tidal.com/track/304673518/u',
    apple: 'https://music.apple.com/us/song/inner-orbit/1697299318',
    youtube: 'https://music.youtube.com/watch?v=N77Ie9HWD3s',
  },
  lumbre: {
    spotify: 'https://open.spotify.com/track/0lbEt5SLf3vAViQ4bI3sg9',
    bandcamp: 'https://brinary.bandcamp.com/track/lumbre',
    tidal: 'https://tidal.com/track/304673519/u',
    apple: 'https://music.apple.com/us/song/lumbre/1697299319',
    youtube: 'https://music.youtube.com/watch?v=ENn9eJ1P0eE',
  },
  'ode-to-janus': {
    spotify: 'https://open.spotify.com/track/0mds2D3ogNeHoqfFs5NF9O',
    bandcamp: 'https://brinary.bandcamp.com/track/ode-to-janus',
    tidal: 'https://tidal.com/track/304673520/u',
    apple: 'https://music.apple.com/us/song/ode-to-janus/1697299320',
    youtube: 'https://music.youtube.com/watch?v=PRCtZOL9HSQ',
  },
  'hit-the-deck': {
    spotify: 'https://open.spotify.com/track/47769xgZ1pAAutltZ2kGcc',
    bandcamp: 'https://brinary.bandcamp.com/track/hit-the-deck',
    tidal: 'https://tidal.com/track/304673521/u',
    apple: 'https://music.apple.com/us/song/hit-the-deck/1697299321',
    youtube: 'https://music.youtube.com/watch?v=7jrtHaQB1m8',
  },
  'castles-fall': {
    spotify: 'https://open.spotify.com/track/6nvDRg4gAlFP55yuvW2sd4',
    bandcamp: 'https://brinary.bandcamp.com/track/castles-fall',
    tidal: 'https://tidal.com/track/304673522/u',
    apple: 'https://music.apple.com/us/song/castles-fall/1697299322',
    youtube: 'https://music.youtube.com/watch?v=qXPJYYCqpXc',
  },
  'checkin-in': {
    spotify: 'https://open.spotify.com/track/4UQMqED8qawMF4ay2KaIRg',
    bandcamp: 'https://brinary.bandcamp.com/track/checkin-in',
    tidal: 'https://tidal.com/track/304673523/u',
    apple: 'https://music.apple.com/us/song/checkin-in/1697299323',
    youtube: 'https://music.youtube.com/watch?v=ndcy94TkdtA',
  },
  'lone-wanderer': {
    spotify: 'https://open.spotify.com/track/7DImDtcJRUyLkAg5nwukXB',
    bandcamp: 'https://brinary.bandcamp.com/track/lone-wanderer',
    tidal: 'https://tidal.com/track/304673524/u',
    apple: 'https://music.apple.com/us/song/lone-wanderer/1697299324',
    youtube: 'https://music.youtube.com/watch?v=ajR9Qy820Gk',
  },
  'noble-gases': {
    spotify: 'https://open.spotify.com/track/23ab879RTDj4DB37uEwtDl',
    bandcamp: 'https://brinary.bandcamp.com/track/noble-gases',
    tidal: 'https://tidal.com/track/304673525/u',
    apple: 'https://music.apple.com/us/song/noble-gases/1697299325',
    youtube: 'https://music.youtube.com/watch?v=m46HZyYMl2g',
  },
  avolition: {
    spotify: 'https://open.spotify.com/track/5GNYqa40NzDzo3YIL6gn22',
    bandcamp: 'https://brinary.bandcamp.com/track/avolition',
    tidal: 'https://tidal.com/track/304673526/u',
    apple: 'https://music.apple.com/us/song/avolition/1697299326',
    youtube: 'https://music.youtube.com/watch?v=iTVNWnlIZ3A',
  },
  'spirits-in-the-walls': {
    spotify: 'https://open.spotify.com/track/5nWdYznCGqe0EXYbp0MVNE',
    bandcamp: 'https://brinary.bandcamp.com/track/spirits-in-the-walls',
    tidal: 'https://tidal.com/track/304673527/u',
    apple: 'https://music.apple.com/us/song/spirits-in-the-walls/1697299327',
    youtube: 'https://music.youtube.com/watch?v=bT1lYu6KNt4',
  },
  'the-day-i-learned-to-do-easy': {
    spotify: 'https://open.spotify.com/track/1ZSTEyWHW35t23xdStNI9U',
    bandcamp: 'https://brinary.bandcamp.com/track/the-day-i-learned-to-do-easy',
    tidal: 'https://tidal.com/track/304673528/u',
    apple: 'https://music.apple.com/us/song/the-day-i-learned-to-do-easy/1697299328',
    youtube: 'https://music.youtube.com/watch?v=FnPkEVgLbLI',
  },
};

export default songLinks;
